import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';

// ─── Axios instance ───────────────────────────────────────────────────────────

export const api = axios.create({
  baseURL: `${BASE_URL}/api`,
  withCredentials: true, // send httpOnly refresh cookie
  headers: { 'Content-Type': 'application/json' },
});

// ─── Token management ─────────────────────────────────────────────────────────

let accessToken: string | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
}

export function getAccessToken() {
  return accessToken;
}

// ─── Request interceptor — attach access token ────────────────────────────────

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// ─── Response interceptor — auto-refresh on 401 ──────────────────────────────

let isRefreshing = false;
let pendingQueue: Array<{ resolve: (token: string) => void; reject: (err: unknown) => void }> = [];

function processPendingQueue(error: unknown, token: string | null) {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token!);
  });
  pendingQueue = [];
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Only auto-refresh if it's a 401, not the refresh endpoint itself, and not already retried
    if (error.response?.status !== 401 || original._retry || original.url === '/auth/refresh') {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingQueue.push({ resolve, reject });
      }).then((token) => {
        original.headers.Authorization = `Bearer ${token}`;
        return api(original);
      });
    }

    original._retry = true;
    isRefreshing = true;

    try {
      const { data } = await api.post<{ accessToken: string }>('/auth/refresh');
      setAccessToken(data.accessToken);
      processPendingQueue(null, data.accessToken);
      original.headers.Authorization = `Bearer ${data.accessToken}`;
      return api(original);
    } catch (refreshError) {
      setAccessToken(null);
      processPendingQueue(refreshError, null);
      // Redirect to login
      window.location.href = '/auth';
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

// ─── Typed API helpers ────────────────────────────────────────────────────────

export interface LoginResponse {
  accessToken: string;
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
    isVerified: boolean;
    trustScore: number;
    avatar: string | null;
  };
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

export const authApi = {
  register: (data: { name: string; email: string; password: string; phone?: string; role?: string }) =>
    api.post<{ userId: string; message: string }>('/auth/register', data),

  login: (data: { email: string; password: string }) =>
    api.post<LoginResponse>('/auth/login', data),

  logout: () => api.post('/auth/logout'),

  refresh: () => api.post<{ accessToken: string }>('/auth/refresh'),

  me: () => api.get('/auth/me'),

  verifyOTP: (data: { userId: string; code: string; purpose: string }) =>
    api.post('/auth/verify-otp', data),

  otpRequest: (identifier: string, purpose: 'login' | 'password_reset') =>
    api.post<{ message: string; userId?: string; devCode?: string }>('/auth/otp/request', { identifier, purpose }),
  otpLogin: (userId: string, code: string) =>
    api.post<LoginResponse>('/auth/otp/login', { userId, code }),
  passwordReset: (userId: string, code: string, password: string) =>
    api.post<{ message: string }>('/auth/password/reset', { userId, code, password }),
};

export const propertyApi = {
  list: (params?: Record<string, unknown>) =>
    api.get('/properties', { params }),

  get: (id: string) => api.get(`/properties/${id}`),

  create: (data: Record<string, unknown>) => api.post('/properties', data),

  update: (id: string, data: Record<string, unknown>) => api.put(`/properties/${id}`, data),

  delete: (id: string) => api.delete(`/properties/${id}`),

  feature: (id: string) => api.post<{ property: unknown; simulated: boolean }>(`/properties/${id}/feature`),
};

export const bookingApi = {
  list: () => api.get('/bookings'),

  get: (id: string) => api.get(`/bookings/${id}`),

  create: (data: { propertyId: string; tokenAmount: number; moveInDate?: string }) =>
    api.post('/bookings', data),

  updateStatus: (id: string, status: string) =>
    api.patch(`/bookings/${id}/status`, { status }),

  sign: (id: string) => api.patch(`/bookings/${id}/sign`),

  report: (id: string, message: string) => api.post(`/bookings/${id}/report`, { message }),
};

export const userApi = {
  list: () => api.get('/users'),
  get: (id: string) => api.get(`/users/${id}`),
  updateMe: (data: Record<string, unknown>) => api.patch('/users/me', data),
  adminUpdate: (id: string, data: Record<string, unknown>) => api.patch(`/users/${id}`, data),
  delete: (id: string) => api.delete(`/users/${id}`),
};

export const nidApi = {
  upload: (data: Record<string, unknown>) => api.post('/nid/upload', data),
  getStatus: () => api.get('/nid/me'),
  getPending: () => api.get('/nid'),
  review: (userId: string, data: { status: 'APPROVED' | 'REJECTED'; adminNote?: string }) =>
    api.patch(`/nid/${userId}/review`, data),
};

export const messApi = {
  getExpenses: () => api.get('/mess/expenses'),
  addExpense: (data: Record<string, unknown>) => api.post('/mess/expenses', data),
  getMeals: (params?: { date?: string }) => api.get('/mess/meals', { params }),
  updateMeal: (data: Record<string, unknown>) => api.put('/mess/meals', data),
  getSummary: () => api.get('/mess/summary'),
};

export interface ReportSummary {
  totalRevenue: number;
  mrr: number;
  gmv: number;
  revenueByPurpose: { purpose: string; amount: number; count: number }[];
  bookingsByStatus: { status: string; count: number }[];
  usersByRole: { role: string; count: number }[];
  newUsersThisMonth: number;
  activeSubscriptions: number;
}
export interface SettingItem { key: string; value: unknown; label: string | null }

export interface AiTemplate {
  key: string;
  label: string;
  model: string;
  prompt: string;
  version: number;
  isActive: boolean;
}
export interface AiUsageRow { templateKey: string; calls: number; failures: number; avgLatencyMs: number }

export interface AdminGateway {
  id: string;
  gateway: string;
  name: string;
  status: 'active' | 'maintenance' | 'disabled';
  isSandbox: boolean;
  merchantId: string;
  callbackUrl: string;
  hasApiKey: boolean;
  hasSecretKey: boolean;
}

export interface PublicGateway {
  gateway: string;
  name: string;
  status: 'active' | 'maintenance' | 'disabled';
  isSandbox: boolean;
}

export const adminApi = {
  getStats: () => api.get('/admin/stats'),
  getActivity: (page?: number) => api.get('/admin/activity', { params: { page } }),
  getGateways: () => api.get<AdminGateway[]>('/admin/gateways'),
  getApprovals: () => api.get('/admin/approvals'),
  aiTemplates: () => api.get<AiTemplate[]>('/admin/ai/templates'),
  updateAiTemplate: (key: string, data: { prompt?: string; isActive?: boolean }) => api.put<AiTemplate>(`/admin/ai/templates/${key}`, data),
  aiUsage: () => api.get<AiUsageRow[]>('/admin/ai/usage'),
  reports: () => api.get<ReportSummary>('/admin/reports/summary'),
  settings: () => api.get<SettingItem[]>('/admin/settings'),
  updateSetting: (key: string, value: unknown) => api.patch<SettingItem>('/admin/settings', { key, value }),
  approveProperty: (id: string) => api.post(`/admin/approvals/${id}/approve`),
  rejectProperty: (id: string, note: string) => api.post(`/admin/approvals/${id}/reject`, { note }),
  updateGateway: (
    gateway: string,
    data: Partial<{ status: string; isSandbox: boolean; merchantId: string; apiKey: string; secretKey: string; callbackUrl: string }>
  ) => api.patch<AdminGateway[]>(`/admin/gateways/${gateway}`, data),
};

export interface InitiateResponse {
  paymentId: string;
  redirectUrl: string | null;
  simulated: boolean;
}

export const paymentApi = {
  gateways: () => api.get<PublicGateway[]>('/payments/gateways'),
  initiate: (data: { bookingId: string; gateway: string; amount: number }) =>
    api.post<InitiateResponse>('/payments/initiate', data),
  get: (id: string) => api.get(`/payments/${id}`),
};

export interface Contract {
  id: string;
  bookingId: string;
  status: 'DRAFT' | 'SENT' | 'SIGNED' | 'CANCELLED' | 'EXPIRED';
  currentVersion: number;
  content: string;
  tenantSignedAt: string | null;
  landlordSignedAt: string | null;
  sentAt: string | null;
}

export const contractApi = {
  byBooking: (bookingId: string) => api.get<Contract | null>(`/contracts/by-booking/${bookingId}`),
  get: (id: string) => api.get<Contract & { versions: unknown[] }>(`/contracts/${id}`),
  create: (bookingId: string) => api.post<Contract>(`/contracts/from-booking/${bookingId}`),
  send: (id: string) => api.post<Contract>(`/contracts/${id}/send`),
  sign: (id: string) => api.post<Contract>(`/contracts/${id}/sign`),
  cancel: (id: string) => api.post<Contract>(`/contracts/${id}/cancel`),
  pdfUrl: (id: string) => `${BASE_URL}/api/contracts/${id}/pdf`,
};

export interface ReviewItem {
  id: string;
  rating: number;
  comment: string | null;
  isHidden?: boolean;
  createdAt: string;
  author?: { name: string; avatar: string | null };
}

export const reviewApi = {
  property: (id: string) => api.get<{ reviews: ReviewItem[]; count: number; avg: number }>(`/reviews/property/${id}`),
  user: (id: string) => api.get<{ reviews: ReviewItem[]; count: number; avg: number }>(`/reviews/user/${id}`),
  create: (data: { subjectType: string; propertyId?: string; subjectUserId?: string; rating: number; comment?: string }) =>
    api.post('/reviews', data),
  moderation: () => api.get<ReviewItem[]>('/reviews/moderation'),
  hide: (id: string) => api.patch(`/reviews/${id}/hide`),
};

export interface ConversationSummary {
  id: string;
  propertyTitle: string | null;
  isReported: boolean;
  other: { id: string; name: string; avatar: string | null } | null;
  lastMessage: string | null;
  updatedAt: string;
}
export interface ChatMessage {
  id: string;
  senderId: string;
  content: string;
  createdAt: string;
}

export const conversationApi = {
  list: () => api.get<ConversationSummary[]>('/conversations'),
  start: (propertyId: string, message: string) => api.post<{ id: string }>('/conversations', { propertyId, message }),
  messages: (id: string) => api.get<ChatMessage[]>(`/conversations/${id}/messages`),
  send: (id: string, content: string) => api.post(`/conversations/${id}/messages`, { content }),
  report: (id: string, note: string) => api.post(`/conversations/${id}/report`, { note }),
  reported: () => api.get<ReportedConversation[]>('/conversations/reported'),
  blockMessage: (id: string) => api.patch(`/conversations/messages/${id}/block`),
};

export interface ReportedConversation {
  id: string;
  reportNote: string | null;
  property?: { title: string } | null;
  messages: { id: string; senderId: string; content: string; isBlocked: boolean; createdAt: string }[];
}

export interface NotificationItem {
  id: string;
  title: string;
  body: string;
  readAt: string | null;
  createdAt: string;
}

export const notificationApi = {
  list: () => api.get<{ items: NotificationItem[]; unread: number }>('/notifications'),
  read: (id: string) => api.patch(`/notifications/${id}/read`),
  readAll: () => api.post('/notifications/read-all'),
};

export interface TicketSummary {
  id: string;
  subject: string;
  category: string;
  priority: string;
  status: string;
  requester?: { name: string; email: string };
  _count?: { messages: number };
  updatedAt: string;
}
export interface TicketMessageItem { id: string; authorId: string; body: string; isInternal: boolean; createdAt: string }

export const ticketApi = {
  list: () => api.get<TicketSummary[]>('/tickets'),
  get: (id: string) => api.get<TicketSummary & { messages: TicketMessageItem[] }>(`/tickets/${id}`),
  create: (data: { subject: string; category?: string; priority?: string; message: string }) => api.post<{ id: string }>('/tickets', data),
  reply: (id: string, body: string, isInternal?: boolean) => api.post(`/tickets/${id}/messages`, { body, isInternal }),
  update: (id: string, data: { status?: string; priority?: string; assignedTo?: string }) => api.patch(`/tickets/${id}`, data),
};

export interface Plan { plan: string; label: string; price: number; durationDays: number | null; perks: string[] }
export interface SubscriptionItem { id: string; plan: string; status: string; price: number; endsAt: string | null }

export const subscriptionApi = {
  plans: () => api.get<Plan[]>('/subscriptions/plans'),
  me: () => api.get<SubscriptionItem[]>('/subscriptions/me'),
  subscribe: (plan: string) => api.post<{ subscription: SubscriptionItem; simulated: boolean }>('/subscriptions/subscribe', { plan }),
  cancel: (id: string) => api.post(`/subscriptions/${id}/cancel`),
};

export const uploadApi = {
  image: (file: File) => {
    const form = new FormData();
    form.append('image', file);
    return api.post<{ url: string; publicId: string }>('/upload/image', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  nid: (file: File) => {
    const form = new FormData();
    form.append('nid', file);
    return api.post<{ url: string; publicId: string }>('/upload/nid', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};
